import {
  Alert,
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import RedirectBackButton from "components/RedirectBackButton";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import {
  ImportJobGraph,
  ImportJobResultCounts,
  ResponseOfFileProcessingSnapshot,
  ResponseOfImportJobGraph,
  ResponseOfImportJobSummary,
} from "services/KnowfullyAdminClient";
import { ImportSourceTypes, JobDataStatus, JobStatus } from "types/common-types";
import ImportsEntityTreeTable from "./ImportsEntityTreeTable";
import ImportsRecordSummaryTable from "./ImportsRecordSummaryTable";
import ImportsResultTiles from "./ImportsResultTiles";
import { ImportsSummaryDetails } from "./ImportsSummaryDetails";

// The list refreshes every 30s, which suits a page you scan. This one is watched while a job drains, and a
// 200-row import finishes inside a couple of minutes, so a 30s tick made the tiles look frozen next to a
// record table that had already moved on. Every panel on this page shares this cadence so they agree.
const POLL_INTERVAL_MS = 5000;

const styles = {
  progressLabel: {
    mb: 1.5,
  },
  fallbackNotice: {
    mb: 2,
  },
};

const isJobInProgress = (jobStatusName?: string) =>
  jobStatusName === JobStatus.Processing ||
  jobStatusName === JobStatus.Unprocessed;

const nonTerminalEntityStatuses: string[] = [
  JobDataStatus.Unprocessed,
  JobDataStatus.Queued,
  JobDataStatus.Processing,
  JobDataStatus.Retrying,
];

const hasEntitiesInProgress = (graph?: ImportJobGraph) =>
  (graph?.nodes ?? []).some((node) =>
    nonTerminalEntityStatuses.includes(node.jobDataStatus ?? "")
  );

// The snapshot always counts JobData rows, which is one row for an entire JSON payload, so a job with 19
// entities reports 0% until the whole payload lands. The result counts instead follow the job's source —
// rows for CSV, entities for JSON — and are what the tiles below the bar already show, so the bar reads from
// them and only falls back to the snapshot before a job has produced anything to count.
const settledProgress = (counts?: ImportJobResultCounts) => {
  const total = counts?.total ?? 0;

  if (total === 0) {
    return undefined;
  }

  const settled =
    (counts?.processed ?? 0) + (counts?.errored ?? 0) + (counts?.cancelled ?? 0);

  return Math.round((settled / total) * 100);
};

export const ImportsSummary = () => {
  const { migrationJobService, importsUrl } = useGlobalContext();
  const { formatMessage: t } = useIntl();

  const { jobId } = useParams<{ jobId: string }>();

  const {
    data: importJobSummary,
    isLoading: isLoadingSummary,
  } = useQuery<ResponseOfImportJobSummary>(
    ["importJobSummary", jobId],
    () => migrationJobService.getImportJobSummary(jobId!),
    {
      // staleTime is Infinity app-wide, so reopening a job would otherwise render the snapshot from the last
      // visit and leave it there until the first poll tick — up to POLL_INTERVAL_MS of visibly stale numbers.
      refetchOnMount: "always",
      // refetchOnWindowFocus is false app-wide, and react-query skips an interval tick entirely while the
      // window is unfocused. Without this the page freezes the moment you look at something else and never
      // recovers on return, which is what made the tiles disagree with the record table below them.
      refetchIntervalInBackground: true,
      refetchInterval: (latest) =>
        isJobInProgress(latest?.data?.jobStatusName) ? POLL_INTERVAL_MS : false,
    }
  );

  const job = importJobSummary?.data;
  useTitle(job?.jobName || t({ id: "importsSummary" }));

  const isJsonImport =
    job?.importJobSourceTypeName === ImportSourceTypes.JSON;

  const {
    data: snapshotResults,
    isLoading: isLoadingSnapshot,
  } = useQuery<ResponseOfFileProcessingSnapshot>(
    ["importJobProcessSnapshot", jobId!],
    () => migrationJobService.getFileProcessingSnapshot(jobId!),
    {
      enabled: jobId !== undefined,
      refetchOnMount: "always",
      refetchIntervalInBackground: true,
      refetchInterval: (latest) =>
        (latest?.data?.progress ?? 0) < 100 ? POLL_INTERVAL_MS : false,
    }
  );

  const {
    data: graphResult,
    isLoading: isLoadingGraph,
    isError: isGraphError,
  } = useQuery<ResponseOfImportJobGraph>(
    ["importJobGraph", jobId!],
    () => migrationJobService.getImportJobGraph(jobId!),
    {
      enabled: jobId !== undefined && isJsonImport,
      refetchOnMount: "always",
      refetchIntervalInBackground: true,
      // Keyed on the graph's own rows rather than the job status, so the final state is always fetched:
      // a stop condition reading the summary query would be cleared the moment that query reports Processed,
      // leaving the tree showing whatever it saw up to one interval earlier.
      refetchInterval: (latest) =>
        hasEntitiesInProgress(latest?.data) ? POLL_INTERVAL_MS : false,
    }
  );

  const progress =
    settledProgress(job?.resultCounts) ?? snapshotResults?.data?.progress ?? 0;
  const isFinished = progress === 100 || job?.jobStatusName === JobStatus.Cancelled;

  // A truncated or failed graph falls back to the record view rather than leaving the page empty; that view
  // also carries the per-record cancel button, which the tree has no equivalent for.
  const showEntityTree =
    isJsonImport && !isGraphError && !graphResult?.data?.truncated;

  return (
    <>
      <RedirectBackButton
        redirectRoute={importsUrl()}
        labelKey="backToListButtonLabel"
      />
      <Box my={3}>
        <Box my={3}>
          {isLoadingSummary ? (
            <CircularProgress size={32} />
          ) : (
            <ImportsSummaryDetails {...job} />
          )}
        </Box>

        <LinearProgress
          color="secondary"
          variant="determinate"
          value={progress}
        />
        <Box mt={2}>
          <Typography
            color={isFinished ? "success.main" : "text.primary"}
            sx={styles.progressLabel}
          >
            {isLoadingSnapshot
              ? t({ id: "calculatingLabel" })
              : t(
                  { id: isFinished ? "importFinishedLabel" : "importRunningLabel" },
                  { progress }
                )}
          </Typography>
          {!isFinished && <CircularProgress size={24} />}
        </Box>
      </Box>

      <ImportsResultTiles counts={job?.resultCounts} />

      {showEntityTree ? (
        <ImportsEntityTreeTable
          graph={graphResult?.data}
          isLoading={isLoadingGraph}
        />
      ) : (
        jobId && (
          <>
            {isJsonImport && (
              <Alert severity="warning" sx={styles.fallbackNotice}>
                <T
                  id={
                    isGraphError
                      ? "entitiesUnavailableMessage"
                      : "entitiesTruncatedMessage"
                  }
                />
              </Alert>
            )}
            <ImportsRecordSummaryTable jobId={jobId} />
          </>
        )
      )}
    </>
  );
};
